// client/src/api/ApiDivisions.js
import flex from './FlexFetch'

const endpoint = '/api/1.0/divisions/'

function normalizeSuccessPayload(result) {
  if (
    result?.success === true &&
    Object.prototype.hasOwnProperty.call(result, 'data')
  ) {
    return result.data
  }
  return result
}

function rethrowNormalizedError(
  error,
  defaultMessage,
  { errorField = 'message' } = {},
) {
  const responseData = error?.response?.data

  if (responseData?.success === false && responseData.error) {
    const strictError = responseData.error
    const details =
      strictError.details && typeof strictError.details === 'object'
        ? strictError.details
        : {}

    error.response.data = {
      ...details,
      code: strictError.code,
      error: errorField === 'code' ? strictError.code : strictError.message,
      message: strictError.message,
      ...(strictError.fields ? { fields: strictError.fields } : {}),
      ...(strictError.retryable !== undefined
        ? { retryable: strictError.retryable }
        : {}),
    }
    error.message = strictError.message || error.message || defaultMessage
  } else {
    const rawError = responseData?.error
    error.message =
      (typeof rawError === 'string' ? rawError : rawError?.message) ||
      error.message ||
      defaultMessage
  }

  throw error
}

async function getAllByLeague(league_id) {
  try {
    const response = await flex.flexFetch(
      `${endpoint}byLeague?league_id=${league_id}`,
    )
    const result = await flex.handleResponse(response)
    return normalizeSuccessPayload(result)
  } catch (error) {
    rethrowNormalizedError(error, 'Failed to fetch divisions')
  }
}

async function create(division) {
  try {
    const response = await flex.flexFetch(endpoint + 'create', {
      method: 'POST',
      body: JSON.stringify(division),
    })
    const result = await flex.handleResponse(response)
    return normalizeSuccessPayload(result)
  } catch (error) {
    rethrowNormalizedError(error, 'Failed to create division')
  }
}

async function remove(id) {
  try {
    const response = await flex.flexFetch(
      `${endpoint}delete?division_id=${id}`,
      {
        method: 'DELETE',
      },
    )
    const result = await flex.handleResponse(response)
    return normalizeSuccessPayload(result)
  } catch (error) {
    rethrowNormalizedError(error, 'Failed to delete division')
  }
}

async function update(division) {
  try {
    const response = await flex.flexFetch(endpoint + 'update', {
      method: 'PUT',
      body: JSON.stringify(division),
    })
    const result = await flex.handleResponse(response)
    return normalizeSuccessPayload(result)
  } catch (error) {
    rethrowNormalizedError(error, 'Failed to update division')
  }
}

async function getByLeagueId(league_id) {
  try {
    const response = await flex.flexFetch(
      `/api/1.0/divisions/getByLeague?league_id=${league_id}`,
    )
    const result = await flex.handleResponse(response)
    return normalizeSuccessPayload(result)
  } catch (error) {
    rethrowNormalizedError(error, 'Failed to fetch league divisions')
  }
}

// Save division assignments as draft
async function saveDraftAssignments({ league_id, changes }) {
  try {
    const response = await flex.flexFetch(`${endpoint}saveDraftAssignments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ league_id, changes }),
    })
    const result = await flex.handleResponse(response)
    return normalizeSuccessPayload(result)
  } catch (error) {
    rethrowNormalizedError(error, 'Failed to save draft assignments', {
      errorField: 'code',
    })
  }
}

// Save and publish division assignments
async function publishAssignments(league_id, assignments) {
  try {
    const response = await flex.flexFetch(
      '/api/1.0/divisions/publishAssignments',
      {
        method: 'POST',
        body: JSON.stringify({ league_id, assignments }),
      },
    )
    const result = await flex.handleResponse(response)
    return normalizeSuccessPayload(result)
  } catch (error) {
    rethrowNormalizedError(error, 'Failed to publish divisions')
  }
}

// Include player_id and matches in getMyDivision
async function getMyDivision(league_id) {
  try {
    const res = await flex.flexFetch(
      `/api/1.0/divisions/getMyDivision?league_id=${league_id}`,
    )
    const result = await flex.handleResponse(res)
    return normalizeSuccessPayload(result)
  } catch (error) {
    rethrowNormalizedError(error, 'Failed to fetch your division')
  }
}

// Get all divisions for the current user (batched)
async function getMyDivisions() {
  try {
    const res = await flex.flexFetch('/api/1.0/divisions/getMyDivisions')
    const result = await flex.handleResponse(res)
    return normalizeSuccessPayload(result)
  } catch (error) {
    rethrowNormalizedError(error, 'Failed to fetch your divisions')
  }
}

const ApiDivisions = {
  getAllByLeague,
  create,
  remove,
  update,
  getByLeagueId,
  saveDraftAssignments,
  publishAssignments,
  getMyDivision,
  getMyDivisions,
}

export default ApiDivisions
